import { cn } from "@istari-frontend/shared";
import { Button } from "@istari-frontend/ui";
import { CircleAlertIcon, RotateCwIcon } from "lucide-react";

import type { ChatError } from "~/ai-chat/model/chat-message";
import { copyToClipboard } from "~/utils";

interface ChatErrorBubbleProps {
  error: ChatError;
  // Re-send the last user turn. Omitted while a send is already in flight.
  onRetry?: () => void;
}

export function ChatErrorBubble({ error, onRetry }: ChatErrorBubbleProps) {
  return (
    <div
      role="alert"
      className="border-destructive/30 bg-destructive/10 text-destructive flex flex-col gap-1.5 rounded-md border px-3 py-2 text-xs"
    >
      <div className="flex items-start gap-2">
        <CircleAlertIcon className="mt-0.5 size-3.5 shrink-0" />
        <span className="leading-snug whitespace-pre-wrap">{error.content}</span>
      </div>
      {onRetry || error.code || error.requestId ? (
        <div className="flex items-center gap-2">
          {onRetry ? (
            <Button
              type="button"
              variant="ghost"
              size="2xs"
              onClick={onRetry}
              className="text-destructive hover:bg-destructive/10 -ml-1 h-auto w-fit gap-1 px-1 py-0.5"
            >
              <RotateCwIcon className="size-3" />
              Retry
            </Button>
          ) : null}
          {error.requestId ? (
            <button
              type="button"
              onClick={() => void copyToClipboard(error.requestId ?? "", "Request id copied.")}
              title="Copy request id for support"
              className="text-destructive/60 hover:text-destructive ml-auto font-mono text-[10px] transition-colors"
            >
              {error.requestId}
            </button>
          ) : null}
          {error.code ? (
            <button
              type="button"
              onClick={() => void copyToClipboard(error.code ?? "", "Error code copied.")}
              title="Copy error code"
              className={cn(
                "text-destructive/60 hover:text-destructive font-mono text-[10px] transition-colors",
                error.requestId ? "" : "ml-auto",
              )}
            >
              {error.code}
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
